import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';

import { VEHICLE_REFERENCES_QUERY } from '../vehicles.gql.tsx';

interface CustomerSummary {
	customerId?: string | null;
	displayName?: string | null;
	isCompany?: boolean | null;
	email?: string | null;
}

interface ContractSummary {
	contractId: string;
	beginDate?: string | null;
	endDate?: string | null;
	isActive: boolean;
	customer?: CustomerSummary | null;
}

interface ContractVehicleSummary {
	contractId: string;
	vehicleId: string;
	contract?: ContractSummary | null;
}

interface ParkingSessionSummary {
	parkingSessionId: string;
	entryDateTime: string;
	exitDateTime?: string | null;
	status: string;
	customer?: CustomerSummary | null;
	contract?: {
		contractId: string;
	} | null;
}

interface VehicleReferencesQueryResponse {
	vehicle?: {
		vehicleId: string;
		plateNumber?: string | null;
		customer?: CustomerSummary | null;
		contractVehicles?: ContractVehicleSummary[] | null;
	} | null;
	parkingSessions?: {
		nodes?: ParkingSessionSummary[] | null;
	} | null;
}

interface VehicleReferencesTabProps {
	vehicleId: string;
}

const formatDateTime = (value?: string | null) => {
	if (!value) {
		return 'N/A';
	}

	try {
		return new Intl.DateTimeFormat(undefined, {
			dateStyle: 'medium',
			timeStyle: 'short'
		}).format(new Date(value));
	} catch {
		return value;
	}
};

const formatDate = (value?: string | null) => {
	if (!value) {
		return 'N/A';
	}

	try {
		return new Intl.DateTimeFormat(undefined, {
			dateStyle: 'medium'
		}).format(new Date(value));
	} catch {
		return value;
	}
};

export function VehicleReferencesTab({ vehicleId }: VehicleReferencesTabProps) {
	const { t } = useTranslation('common');
	const { t: tV } = useTranslation('vehicles');
	const { t: tPS } = useTranslation('parkingSessions');

	const { data, loading, error } =
		useQuery<VehicleReferencesQueryResponse>(VEHICLE_REFERENCES_QUERY, {
			variables: { id: vehicleId }
		});

	const { shouldShowLoading } = useDelayedLoading(loading, {
		delay: 100,
		showInitialLoading: true
	});

	const vehicle = data?.vehicle;
	const parkingSessions = data?.parkingSessions?.nodes ?? [];
	const contractVehicles = vehicle?.contractVehicles ?? [];

	const parkingSessionItems = useMemo(() => {
		return parkingSessions.map(session => {
			const exitLabel = session.exitDateTime
				? formatDateTime(session.exitDateTime)
				: tV('references.inProgress');
			const statusLabel = session.status === 'CANCELED'
				? tPS('details.canceledSession')
				: session.status === 'EXITED'
					? tPS('details.exitedSession')
					: tPS('details.insideSession');

			return {
				...session,
				label: `${formatDateTime(session.entryDateTime)} -> ${exitLabel} | ${statusLabel}`
			};
		});
	}, [parkingSessions, tPS, tV]);

	const contractItems = useMemo(() => {
		return contractVehicles
			.map(contractVehicle => {
				const { contract } = contractVehicle;
				if (!contract) return null;

				const start = formatDate(contract.beginDate);
				const end = contract.endDate
					? formatDate(contract.endDate)
					: tV('references.openEnded');
				const statusLabel = contract.isActive
					? t('status.active')
					: t('status.inactive');

				return {
					contractId: contract.contractId,
					label: `${start} -> ${end} | ${statusLabel}`,
					customer: contract.customer
				};
			})
			.filter(Boolean) as Array<{
			contractId: string;
			label: string;
			customer?: CustomerSummary | null;
		}>;
	}, [contractVehicles, t, tV]);

	const customerItems = useMemo(() => {
		type CustomerSource = 'owner' | 'contract' | 'parkingSession';

		const addCustomer = (
			collection: Map<string, { customer: CustomerSummary; sources: Set<CustomerSource> }>,
			customer: CustomerSummary | null | undefined,
			source: CustomerSource
		) => {
			if (!customer) return;

			const key = customer.customerId ?? customer.displayName;
			if (!key) return;

			const existing = collection.get(key);
			if (existing) {
				existing.sources.add(source);
				return;
			}

			collection.set(key, {
				customer,
				sources: new Set([source])
			});
		};

		const customers = new Map<
			string,
			{ customer: CustomerSummary; sources: Set<CustomerSource> }
		>();

		addCustomer(customers, vehicle?.customer, 'owner');
		contractItems.forEach(contract =>
			addCustomer(customers, contract.customer, 'contract')
		);
		parkingSessions.forEach(session =>
			addCustomer(customers, session.customer, 'parkingSession')
		);

		const sourceLabels: Record<CustomerSource, string> = {
			owner: tV('references.owner'),
			contract: tV('references.contractCustomer'),
			parkingSession: tV('references.sessionCustomer')
		};

		return Array.from(customers.values()).map(item => {
			const labelParts = Array.from(item.sources).map(
				source => sourceLabels[source] ?? source
			);

			return {
				customerId: item.customer.customerId ?? undefined,
				displayName:
					item.customer.displayName ??
					item.customer.customerId ??
					t('labels.not_set'),
				sourceLabel: labelParts.join(', ')
			};
		});
	}, [contractItems, parkingSessions, t, tV, vehicle?.customer]);

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-48 h-6 rounded bg-muted animate-pulse" />
				<div className="w-32 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-2">
					<div className="w-full h-4 rounded bg-muted animate-pulse" />
					<div className="w-3/4 h-4 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	if (!loading && (error || !vehicle)) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				{tV('details.loadError')}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<DetailSection
				title={tV('references.parkingSessionsTitle')}
				rowCount={parkingSessionItems.length}
			>
				{parkingSessionItems.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4">
						{tV('references.noParkingSessions')}
					</p>
				) : (
					<DetailFieldsGrid>
						{parkingSessionItems.map(session => (
							<EntityDetailField
								key={session.parkingSessionId}
								label={tV('references.parkingSession')}
								value={session.label}
								entityId={session.parkingSessionId}
								entityType="parkingSessions"
							/>
						))}
					</DetailFieldsGrid>
				)}
			</DetailSection>

			<DetailSection
				title={tV('references.contractsTitle')}
				rowCount={contractItems.length}
			>
				{contractItems.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4">
						{tV('references.noContracts')}
					</p>
				) : (
					<DetailFieldsGrid>
						{contractItems.map(contract => (
							<EntityDetailField
						key={contract.contractId}
								label={tV('references.contract')}
								value={contract.label}
								entityId={contract.contractId}
								entityType="contracts"
							/>
						))}
					</DetailFieldsGrid>
				)}
			</DetailSection>

			<DetailSection
				title={tV('references.customersTitle')}
				rowCount={customerItems.length}
			>
				{customerItems.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4">
						{tV('references.noCustomers')}
					</p>
				) : (
					<DetailFieldsGrid>
						{customerItems.map(customer => (
							<EntityDetailField
								key={customer.customerId ?? customer.displayName}
								label={tV('references.customer')}
								value={
									customer.sourceLabel
										? `${customer.displayName} - ${customer.sourceLabel}`
										: customer.displayName
								}
								entityId={customer.customerId}
								entityType="customers"
							/>
						))}
					</DetailFieldsGrid>
				)}
			</DetailSection>
		</div>
	);
}
